import { useEffect } from 'react';

/**
 * Attaches an IntersectionObserver to every <section> and .tb-wrap on the page.
 * Elements already in the viewport on load are made visible immediately (no flash).
 * Elements below the fold get a "reveal" class and animate in as you scroll.
 *
 * Route pages are lazy-loaded (React.lazy + Suspense), so their <section>s often
 * don't exist in the DOM yet when this effect first runs. A MutationObserver
 * keeps watching so sections mounted later (lazy chunks, async data) still get
 * tagged instead of silently skipping the reveal animation.
 */
const useScrollReveal = () => {
  useEffect(() => {
    const SELECTOR = 'section, .tb-wrap';
    const tagged = new WeakSet();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.07,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const tag = (el) => {
      if (tagged.has(el)) return;
      tagged.add(el);
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
      } else {
        el.classList.add('reveal');
        io.observe(el);
      }
    };

    document.querySelectorAll(SELECTOR).forEach(tag);

    const mo = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.matches?.(SELECTOR)) tag(node);
          node.querySelectorAll?.(SELECTOR).forEach(tag);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
};

export default useScrollReveal;
